import { Box, Button, FormControl, IconButton, MenuItem, Select, Stack, Tooltip, Typography } from '@mui/material'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { WeatherGroup } from '../../models/WeatherGroup.model'

type WeatherGroupPriorityListProps = {
  items: { weatherGroupId: number; priority: number }[]
  weatherGroups: WeatherGroup[]
  onChange: (index: number, weatherGroupId: number) => void
  onRemove: (index: number) => void
  onAdd: () => void
  onReorder: (fromIndex: number, toIndex: number) => void
}
/**
 * @description Ordered, drag-to-reorder list of weather groups used by the status algorithm. The first row has the
 * highest priority.
 */
const WeatherGroupPriorityList = (props: WeatherGroupPriorityListProps) => {
  const { items, weatherGroups, onChange, onRemove, onAdd, onReorder } = props

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData('text/plain', index.toString())
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    const draggedIndex = Number(e.dataTransfer.getData('text/plain'))
    if (isNaN(draggedIndex) || draggedIndex === dropIndex) return
    onReorder(draggedIndex, dropIndex)
  }

  return (
    <Stack spacing={1}>
      {items.map((item, index) => (
        <Stack
          key={index}
          direction={{ zero: 'column', xs: 'row' }}
          spacing={1}
          alignItems={{ zero: 'stretch', xs: 'center' }}
          draggable
          onDragStart={(e) => handleDragStart(e, index)}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, index)}
          sx={{
            cursor: 'grab',
            p: 1,
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            '&:hover': {
              backgroundColor: 'secondary.main',
            },
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <DragIndicatorIcon sx={{ color: 'grey.500' }} />
            <Box
              sx={{
                minWidth: 32,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                textAlign: 'center',
                bgcolor: index === 0 ? 'primary.main' : 'grey.800',
                color: index === 0 ? 'primary.contrastText' : 'common.white',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {index + 1}
              </Typography>
            </Box>
          </Stack>
          <FormControl fullWidth size="small">
            <Select
              fullWidth
              displayEmpty
              value={item.weatherGroupId || ''}
              inputProps={{ 'aria-label': `Weather group priority ${index + 1}` }}
              onChange={(e) => onChange(index, Number(e.target.value))}
            >
              {weatherGroups.map((eachWeatherGroup) => (
                <MenuItem
                  key={eachWeatherGroup.id}
                  value={eachWeatherGroup.id}
                  disabled={items.some((w, i) => w.weatherGroupId === eachWeatherGroup.id && i !== index)}
                >
                  {eachWeatherGroup.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Remove">
            <IconButton aria-label={`Remove weather group ${index + 1}`} onClick={() => onRemove(index)}>
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      ))}
      <Button variant="outlined" fullWidth onClick={onAdd} disabled={weatherGroups.length === items.length}>
        + Add Weather Group
      </Button>
    </Stack>
  )
}
export default WeatherGroupPriorityList
